import { FC, useState } from "react";
import { Box, Button, CircularProgress, Dialog, Typography } from "@mui/material";
import { LogoutOutlined } from "@mui/icons-material";
import { toast } from "react-toast";
import { useStoreContext } from "../../hooks/Contexts";
import ButtonBlue from "../form/button-blue/Component";
import AccountBarStyle, { LogoutDialogStyle } from "./Style";

const shortenAddress = (address: string) =>
  address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;

const actionButtonSx = { flex: 1, minWidth: 0 };

const AccountBar: FC = (): JSX.Element => {
  const { store, logout } = useStoreContext();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const { email, wallet } = store.cart.user;
  // Email sign-ins show the address they used; external wallets only have the wallet address.
  const account = email || (wallet ? shortenAddress(wallet) : "");

  const closeDialog = () => {
    if (!isLoggingOut) setIsDialogOpen(false);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    toast.success("You have been logged out.");
  };

  return (
    <AccountBarStyle>
      <Box className="account-bar-content">
        {account && (
          <Box className="account-bar-identity" title={email || wallet}>
            <Typography variant="textSmallNormal" className="account-bar-label">
              Signed in as
            </Typography>
            <Typography variant="textRegularSemibold" className="account-bar-account" noWrap>
              {account}
            </Typography>
          </Box>
        )}
        <Button
          className="account-bar-logout-button"
          variant="outlined"
          startIcon={<LogoutOutlined />}
          onClick={() => setIsDialogOpen(true)}
        >
          Log out
        </Button>
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        sx={{ zIndex: 1000001 }}
        PaperProps={{ sx: { borderRadius: "24px", margin: "16px", width: "100%", maxWidth: "440px" } }}
      >
        <LogoutDialogStyle>
          <Box className="logout-dialog-icon">
            <LogoutOutlined />
          </Box>
          <Typography id="logout-dialog-title" variant="h6" component="h2">
            Log out of your wallet?
          </Typography>
          <Typography
            id="logout-dialog-description"
            variant="textRegularNormal"
            component="p"
            className="logout-dialog-text"
          >
            Your cards stay safe in your wallet. To view them again, just sign in with the same email
            or wallet.
          </Typography>
          <Box className="logout-dialog-actions">
            <ButtonBlue
              props={{ variant: "outlined", disabled: isLoggingOut, autoFocus: true, sx: actionButtonSx }}
              onClick={closeDialog}
            >
              Cancel
            </ButtonBlue>
            <ButtonBlue
              props={{
                variant: "contained",
                disabled: isLoggingOut,
                sx: { ...actionButtonSx, "&.Mui-disabled": { color: "#fff", opacity: 0.8 } },
              }}
              onClick={handleLogout}
            >
              {isLoggingOut ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ marginRight: "10px" }} />
                  Logging out…
                </>
              ) : (
                "Log out"
              )}
            </ButtonBlue>
          </Box>
        </LogoutDialogStyle>
      </Dialog>
    </AccountBarStyle>
  );
};

export default AccountBar;
